import clsx from 'clsx'
import { useEffect, useMemo, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'

import { usePostByTenantOrderCorrectionMutation } from '@/api/rtk-api'
import {
  Alert,
  CheckboxField,
  LinkButton,
  OperationButton,
  RadioFieldSet,
  ToastNotification
} from '@/components/core-v2'
import { TENANT } from '@/const'

import { useOrdersFiltersContext } from '../assets-filter-list/orders-filter-context'
import { ConfirmedOrder, isBuyOrder } from '../orders-detail/orders-types'
import classes from './price-correction-form.module.scss'
import { PriceCorrectionGrid } from './price-correction-grid'

export interface PriceCorrectionFormProps {
  onClose: () => void
  order: ConfirmedOrder
}

const priceCorrectionId = 'priceCorrection'
const applyCorrectionId = 'applyCorrection'
const submitConfirmationId = 'submitConfirmation'

export const PriceCorrectionForm = ({ onClose, order }: PriceCorrectionFormProps) => {
  const { asset } = useOrdersFiltersContext()
  const [showToast, setShowToast] = useState(false)
  const [mutate, { isLoading, isSuccess }] = usePostByTenantOrderCorrectionMutation()
  const form = useForm({ mode: 'onBlur', reValidateMode: 'onChange' })
  const { display: correctionDisplay, numerical: correctionValue } = form.watch(priceCorrectionId, {
    numerical: undefined,
    display: ''
  })
  const appliedCorrection = form.watch(applyCorrectionId)
  const confirmation = form.watch(submitConfirmationId)

  const { outsideRange, worstOffPrice } = useMemo(() => {
    if (!correctionValue) return { outsideRange: false, worstOffPrice: false }
    const price = order.executionPrice
    const isBuy = isBuyOrder(order)

    return {
      worstOffPrice: isBuy ? correctionValue > price : price > correctionValue,
      outsideRange: isBuy
        ? correctionValue < price - price * 0.1
        : price + price * 0.1 < correctionValue
    }
  }, [correctionValue, order])

  useEffect(() => {
    form.setValue('applyCorrection', worstOffPrice ? 'technical' : '')
  }, [form, worstOffPrice])

  useEffect(() => {
    if (!correctionDisplay) {
      form.reset()
    }
  }, [correctionDisplay, form])

  useEffect(() => {
    if (isSuccess) {
      setShowToast(true)
      onClose()
    }
  }, [isSuccess, onClose])
  const onSubmit = data => {
    mutate({
      apiNewAggregatedOrderCorrection: {
        assetId: asset.assetId,
        executionPrice: data[priceCorrectionId].numerical,
        isAppliedToAccounts: data[applyCorrectionId] === 'client',
        orderId: order.orderId
      },
      tenant: TENANT
    })
  }

  return (
    <FormProvider {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)}>
        <PriceCorrectionGrid order={order} />
        <div className={clsx(classes.formInputs, 'mt-5')}>
          {worstOffPrice && (
            <Alert type="warning">
              Warning: this price correction would negatively impact the clients' positions,
              therefore it will be applied to the technical account
            </Alert>
          )}
          {outsideRange && (
            <Alert type="warning">
              Warning: this price is different from the original price by more than 10%
            </Alert>
          )}
          <RadioFieldSet
            isDisabled={!correctionValue || worstOffPrice}
            legend=""
            name={applyCorrectionId}
            options={[
              { label: 'Apply correction to client accounts', value: 'client' },
              { label: 'Apply correction to technical account', value: 'technical' }
            ]}
          />
          <CheckboxField
            id={submitConfirmationId}
            isDisabled={!appliedCorrection}
            label="Please confirm price per unit. Once set it cannot be changed."
          />
          <div className={classes.buttons}>
            <OperationButton
              isDisabled={!confirmation}
              isLoading={isLoading}
              operationType="progress"
              type="submit"
            >
              Submit
            </OperationButton>
            <LinkButton underline className={classes.close} onClick={() => onClose()}>
              Close without submitting
            </LinkButton>
          </div>
        </div>
      </form>
      <ToastNotification
        description="corrected price per unit successfully!"
        notificationType="success"
        open={showToast}
        title="Corrected order price"
        onClose={() => setShowToast(false)}
      />
    </FormProvider>
  )
}
